import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AssetsService } from '@app/shared/services/assets.service';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { GeoSnackBarService } from '@app/shared/services/snack-bar/snack-bar.service';
import { ErrorService } from '@app/shared/services/error.service';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { ImageUploaderStyle } from '@app/shared/components/form-builder/data.interface';
import { TranslateService } from '@ngx-translate/core';

const ASPECT_RATIO = [1.77, 1.78, 1.79];

@Component({
  selector: 'app-image-uploader',
  templateUrl: './image-uploader.component.html',
  styleUrls: ['./image-uploader.component.scss'],
})
export class ImageUploaderComponent implements OnInit, OnDestroy {
  @Input() imageMinSize = 7000000;
  @Input() originalImage: string | ArrayBuffer | null = '';
  @Input() isLoading = false;
  @Input() imageContainer?: ImageUploaderStyle = 'circular';
  @Input() ratioImageValidation = false;
  @Input() sizeImageValidation = false;
  @Input() fromGalleryDialog = false;

  @Output() loadingEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() imageChosenEmit: EventEmitter<string> = new EventEmitter<string>();
  @Output() deleteImage: EventEmitter<string> = new EventEmitter<string>();
  isDeleting = false;
  imagePreview: File | undefined | null;
  errorUpload = '';
  imageSubscription$: Subscription = new Subscription();
  image: string | ArrayBuffer | null = '';

  currentCropperImage: File | undefined;
  croppedImage: ImageCroppedEvent | undefined | null;
  fileName: string = '';
  isCropperUploadButtonDisabled = true;
  croppedImageSize: { kb: number; bytes: number } = {
    kb: 0,
    bytes: 0,
  };

  constructor(
    private assetsService: AssetsService,
    private snackBarService: GeoSnackBarService,
    private errorService: ErrorService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.image = this.originalImage;
  }

  setImage(files: FileList) {
    this.errorUpload = '';
    this.isDeleting = false;
    if (!files || files.length === 0) {
      return;
    }
    const errorMessages: string[] = [];
    const filesToUpload: File[] = [];
    const filesToPreview: { file: File; reader: FileReader }[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i)!;
      const img = new Image();
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        img.src = window.URL.createObjectURL(file);
        img.onload = () => {
          // Size validation
          if (
            this.sizeImageValidation &&
            Number(file.size) < this.imageMinSize
          ) {
            errorMessages.push(
              this.translateService.instant('GENERAL.IMAGE') +
                file.name +
                this.translateService.instant('GENERAL.MORE_SIZE')
            );

            this.snackBarService.error({
              title: this.translateService.instant('GENERAL.ERROR'),
              message:
                this.translateService.instant('GENERAL.IMAGE') +
                file.name +
                this.translateService.instant('GENERAL.MORE_SIZE'),
            });
            return;
          }
          // Ratio validation
          if (this.ratioImageValidation) {
            const height = img.height;
            const width = img.width;
            const aspectRatio = Number((width / height).toFixed(2));
            if (!ASPECT_RATIO.includes(aspectRatio)) {
              // For now, only handle one crop at a time
              if (!this.currentCropperImage) {
                this.setLoading(false);
                this.currentCropperImage = file;
                this.imagePreview = file;
                this.fileName = file.name;
              }
              return;
            }
          }
          // If valid, add to upload list
          filesToUpload.push(file);
          filesToPreview.push({ file, reader });
          // If this is the first valid file, set preview
          if (!this.imagePreview) {
            this.image = reader.result;
            this.imagePreview = file;
            this.fileName = file.name || 'cropped-file.png';
          }
        };
      };
    }
    // After all readers finish, upload valid files
    setTimeout(() => {
      if (filesToUpload.length > 0) {
        this.setLoading(true);
        filesToUpload.forEach((file) => {
          this.uploadImage(file);
        });
      } else {
        this.setLoading(false);
      }
      if (errorMessages.length > 0) {
        this.errorUpload = errorMessages[0];
      }
    }, 500);
  }

  uploadImage(imagePreview: File) {
    this.imageSubscription$ = this.assetsService
      .uploadFile(imagePreview)
      .pipe(
        tap((imageId: string) => {
          this.setLoading(false);
          this.imageChosenEmit.emit(imageId);
        }),
        catchError(() => {
          this.setLoading(false);
          this.errorUpload = '';

          if (this.originalImage) {
            this.image = this.originalImage;
          } else {
            this.image = null;
            this.imagePreview = null;
          }
          this.snackBarService.error({
            title: this.translateService.instant('GENERAL.ERROR'),
            message: this.translateService.instant('GENERAL.INTERNAL_ERROR'),
          });
          return EMPTY;
        })
      )
      .subscribe();
  }

  removePhoto() {
    this.isDeleting = true;
    this.image = null;
    this.imagePreview = null;
    if (this.originalImage) {
      this.deleteImage.emit('');
    }
  }

  uploadCroppedImage() {
    if (this.croppedImage?.blob) {
      this.setLoading(true);
      const file = this.createFileFromBlob(this.croppedImage.blob);
      this.uploadImage(file);
    }
  }

  private setLoading(value: boolean) {
    this.isLoading = value;
    this.loadingEmitter.emit(value);
  }

  private createFileFromBlob(blob: Blob) {
    const extension = this.getFileExtensionFromMime(blob.type);
    const baseName = this.fileName?.includes('.')
      ? this.fileName.replace(/\.[^/.]+$/, '')
      : this.fileName || 'cropped-file';
    const fileCreated = new File([blob], `${baseName}.${extension}`, {
      type: blob.type,
    });
    return fileCreated;
  }

  private getFileExtensionFromMime(mimeType: string): string {
    switch (mimeType) {
      case 'image/jpeg':
        return 'jpg';
      case 'image/png':
        return 'png';
      case 'image/webp':
        return 'webp';
      case 'image/gif':
        return 'gif';
      case 'image/bmp':
        return 'bmp';
      case 'image/svg+xml':
        return 'svg';
      case 'image/tiff':
        return 'tiff';
      default:
        return 'png';
    }
  }

  // #region <image-cropper> method

  async imageCropped(event: ImageCroppedEvent) {
    if (event.objectUrl) {
      if (event.objectUrl) {
        this.croppedImage = event;
        let blob = event.blob;
        this.croppedImageSize.bytes = this.croppedImage?.blob?.size || 1;
        let objectUrl = event.objectUrl;
        const MAX_SIZE = 1048576; // 1MB in bytes
        if (blob && blob.size > MAX_SIZE) {
          // Compress the image using canvas
          const img = document.createElement('img');
          img.src = objectUrl;
          await new Promise((resolve) => (img.onload = resolve));
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0);
          let quality = 0.92;
          let newBlob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob(resolve, 'image/jpeg', quality)
          );
          // Reduce quality until under 1MB or minimum quality
          while (newBlob && newBlob.size > MAX_SIZE && quality > 0.1) {
            quality -= 0.05;
            newBlob = await new Promise<Blob | null>((resolve) =>
              canvas.toBlob(resolve, 'image/jpeg', quality)
            );
          }
          if (newBlob && newBlob.size <= MAX_SIZE) {
            blob = newBlob;
            objectUrl = URL.createObjectURL(newBlob);
          }
        }
        this.croppedImage = { ...event, blob, objectUrl };
        this.croppedImageSize.bytes = blob?.size || 1;
        this.croppedImageSize.kb = Math.round(
          this.croppedImageSize.bytes / 1024
        );
        this.croppedImageSize.kb = Math.round(
          this.croppedImageSize.bytes / 1024
        );

        this.isCropperUploadButtonDisabled =
          this.isCropperUploadButtonDisabled =
            this.imageMinSize > this.croppedImageSize.bytes;
        this.croppedImageSize.bytes > MAX_SIZE;
      }
    }
  }

  // #endregion <image-cropper> methods
  // #endregion <image-cropper> methods Modified file

  // #endregion <image-cropper> methods

  ngOnDestroy() {
    this.imageSubscription$.unsubscribe();
  }
}
